import { ReactNode, useMemo } from 'react';

import { EditEntitySection } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreen.types';
import { getEntityTypeFromEntityKind } from '@/modules/entities/utils/getEntityTypeFromEntityKind';
import { ClientPresentationDesignerV2_EntityFragment } from '@/modules/presentation/clientPresentation/graphql/ClientPresentationDesignerV2.generated';

import { EditEntitySectionButton } from '../../../presentationEntityActions/PresentationEntityActionButtons';
import { usePresentationEntityActionsContext } from '../../../presentationEntityActions/presentationEntityActions.context';

interface UseInsuranceToolbarParams {
  entity: ClientPresentationDesignerV2_EntityFragment;
  /** When false (e.g. a view-only viewer), no toolbar is returned. */
  canEdit: boolean;
}

/**
 * Builds the "Edit insurance policies" toolbar for the insurance-policies slide
 * from a plain entity, so both the presentation designer and the single-entity
 * export preview can offer the edit affordance. Returns `undefined` when editing
 * is off or the entity exposes no insurance-policies edit section.
 */
export function useInsuranceToolbar({
  entity,
  canEdit,
}: UseInsuranceToolbarParams): ReactNode {
  const rowActions = usePresentationEntityActionsContext();
  const canEditEntity = canEdit && !!rowActions;
  const entityType = getEntityTypeFromEntityKind(entity.kind);

  // Only offer the edit button when the entity actually exposes an insurance
  // policies edit section (e.g. ILITs). Resolving against the entity's own
  // section list — the same source the edit-entity dropdown uses — keeps the
  // button in lockstep with the modal.
  const insuranceSection = canEditEntity
    ? rowActions
        .getEditEntitySections(entityType)
        .find(({ section }) => section === EditEntitySection.INSURANCE_POLICIES)
        ?.section
    : undefined;

  return useMemo<ReactNode>(() => {
    if (!insuranceSection) {
      return undefined;
    }
    return (
      <EditEntitySectionButton
        target={{ entityId: entity.id, entityType }}
        section={insuranceSection}
        label="Edit insurance policies"
      />
    );
  }, [insuranceSection, entity.id, entityType]);
}
